import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useModalHistory } from 'hooks/useModalHistory';
import { Avatar } from 'components/_new/Avatar';
import { dateFormat } from 'utils/dateFormat';
import { roundFixed } from 'utils/number';
import { Button } from '../Button';
import './ImageGalleryViewer.scss';

export type GalleryImage = {
	id?: number | string;
	thumbUrl: string;
	imageUrl: string;
	authorName?: string;
	authorAvatar?: string | null;
	sentAt?: Date | string | null;
	originalFileName?: string;
	fileSize?: number;
};

type ImageGalleryViewerProps = {
	images: GalleryImage[];
	activeIndex: number;
	visible: boolean;
	onClose: () => void;
	onChangeIndex: (index: number) => void;
};

export const ImageGalleryViewer = ({
	images,
	activeIndex,
	visible,
	onClose,
	onChangeIndex,
}: ImageGalleryViewerProps) => {
	useModalHistory(visible, onClose);
	const IMAGE_TRANSITION_MS = 240;
	const MOBILE_BREAKPOINT = 768;
	const SWIPE_THRESHOLD = 40;
	const MOBILE_CONTROLS_VISIBILITY_MS = 1800;
	const { t } = useTranslation();
	const thumbRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const touchStartXRef = useRef<number | null>(null);
	const mobileControlsTimeoutRef = useRef<number | null>(null);

	const normalizedIndex = useMemo(() => {
		if (images.length === 0) return 0;
		return Math.max(0, Math.min(activeIndex, images.length - 1));
	}, [activeIndex, images.length]);

	const activeImage = images[normalizedIndex];
	const previousIndexRef = useRef(normalizedIndex);
	const pendingDirectionRef = useRef<'forward' | 'backward' | null>(null);
	const enteringImageRef = useRef<HTMLImageElement | null>(null);
	const leavingImageRef = useRef<HTMLImageElement | null>(null);
	const [renderedImage, setRenderedImage] = useState(activeImage);
	const [leavingImage, setLeavingImage] = useState<GalleryImage | null>(null);
	const [transitionDirection, setTransitionDirection] = useState<'forward' | 'backward'>('forward');
	const [isMobileView, setIsMobileView] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
	const [mobileDetailsOpen, setMobileDetailsOpen] = useState(false);
	const [mobileControlsVisible, setMobileControlsVisible] = useState(false);
	let mobileNavStateClassName = '';
	if (isMobileView) {
		mobileNavStateClassName = mobileControlsVisible ? ' is-visible' : ' is-hidden';
	}

	const flashMobileControls = () => {
		if (!isMobileView) {
			return;
		}
		if (mobileControlsTimeoutRef.current) {
			window.clearTimeout(mobileControlsTimeoutRef.current);
		}
		setMobileControlsVisible(true);
		mobileControlsTimeoutRef.current = window.setTimeout(() => {
			setMobileControlsVisible(false);
		}, MOBILE_CONTROLS_VISIBILITY_MS);
	};

	const requestIndexChange = (nextIndex: number) => {
		pendingDirectionRef.current = nextIndex > normalizedIndex ? 'forward' : 'backward';
		flashMobileControls();
		onChangeIndex(nextIndex);
	};

	const toggleMobileDetails = () => {
		setMobileDetailsOpen((prev) => !prev);
	};

	useEffect(() => {
		if (!visible) return undefined;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault();
				event.stopPropagation();
				if (typeof event.stopImmediatePropagation === 'function') {
					event.stopImmediatePropagation();
				}
				onClose();
				return;
			}
			if (event.key === 'ArrowRight' && normalizedIndex < images.length - 1) {
				event.preventDefault();
				requestIndexChange(normalizedIndex + 1);
			}
			if (event.key === 'ArrowLeft' && normalizedIndex > 0) {
				event.preventDefault();
				requestIndexChange(normalizedIndex - 1);
			}
		};

		window.addEventListener('keydown', handleKeyDown, true);
		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener('keydown', handleKeyDown, true);
		};
	}, [visible, onClose, onChangeIndex, normalizedIndex, images.length]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth <= MOBILE_BREAKPOINT);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (!visible) return;
		thumbRefs.current[normalizedIndex]?.scrollIntoView({
			behavior: 'smooth',
			inline: 'center',
			block: 'nearest',
		});
	}, [visible, normalizedIndex]);

	useEffect(() => {
		if (!visible) {
			setMobileDetailsOpen(false);
			setMobileControlsVisible(false);
			if (mobileControlsTimeoutRef.current) {
				window.clearTimeout(mobileControlsTimeoutRef.current);
				mobileControlsTimeoutRef.current = null;
			}
		}
	}, [visible]);

	useEffect(() => {
		if (!visible) {
			return;
		}
		if (isMobileView) {
			setMobileControlsVisible(false);
		} else {
			setMobileControlsVisible(true);
		}

		// eslint-disable-next-line consistent-return
		return () => {
			if (mobileControlsTimeoutRef.current) {
				window.clearTimeout(mobileControlsTimeoutRef.current);
				mobileControlsTimeoutRef.current = null;
			}
		};
	}, [isMobileView, visible]);

	useEffect(() => {
		if (!visible) {
			setRenderedImage(activeImage);
			setLeavingImage(null);
			previousIndexRef.current = normalizedIndex;
			return undefined;
		}

		if (renderedImage.id === activeImage.id && renderedImage.imageUrl === activeImage.imageUrl) {
			previousIndexRef.current = normalizedIndex;
			return undefined;
		}

		const nextDirection =
			pendingDirectionRef.current || (normalizedIndex > previousIndexRef.current ? 'forward' : 'backward');
		setTransitionDirection(nextDirection);
		setLeavingImage(renderedImage);
		setRenderedImage(activeImage);
		previousIndexRef.current = normalizedIndex;
		pendingDirectionRef.current = null;

		const timeout = window.setTimeout(() => {
			setLeavingImage(null);
		}, IMAGE_TRANSITION_MS);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [IMAGE_TRANSITION_MS, activeImage, normalizedIndex, renderedImage, visible]);

	useEffect(() => {
		if (!visible) {
			return;
		}

		const enterNode = enteringImageRef.current;
		const leaveNode = leavingImageRef.current;
		const enterFromX = transitionDirection === 'forward' ? 18 : -18;
		const leaveToX = transitionDirection === 'forward' ? -18 : 18;

		enterNode?.animate(
			[
				{ opacity: 0, transform: `translateX(${enterFromX}px)` },
				{ opacity: 1, transform: 'translateX(0px)' },
			],
			{
				duration: IMAGE_TRANSITION_MS,
				easing: 'ease-out',
				fill: 'both',
			}
		);

		leaveNode?.animate(
			[
				{ opacity: 1, transform: 'translateX(0px)' },
				{ opacity: 0, transform: `translateX(${leaveToX}px)` },
			],
			{
				duration: IMAGE_TRANSITION_MS,
				easing: 'ease-out',
				fill: 'both',
			}
		);
	}, [IMAGE_TRANSITION_MS, leavingImage, renderedImage, transitionDirection, visible]);

	if (!visible || images.length === 0) {
		return null;
	}

	const formattedFileSize =
		typeof activeImage.fileSize === 'number' && activeImage.fileSize > 0
			? `${roundFixed(activeImage.fileSize / 1024, 2)} KB`
			: null;
	const showSideArrows = images.length > 2;
	const showMetaPanel = Boolean(
		activeImage.authorName || activeImage.sentAt || activeImage.originalFileName || formattedFileSize
	);
	const showMobileExtraDetails = isMobileView && mobileDetailsOpen;
	const showDetailsSection = !isMobileView || showMobileExtraDetails;
	const detailsContent = (
		<>
			{activeImage.sentAt ? (
				<div className="image-gallery-viewer__meta-item">
					<div className="image-gallery-viewer__meta-label">{t('chat.imageSentAt')}</div>
					<div className="image-gallery-viewer__meta-value">{dateFormat(activeImage.sentAt, 'full')}</div>
				</div>
			) : null}
			{activeImage.originalFileName ? (
				<div className="image-gallery-viewer__meta-item">
					<div className="image-gallery-viewer__meta-label">{t('chat.imageFileName')}</div>
					<div className="image-gallery-viewer__meta-value image-gallery-viewer__meta-value-file-name">
						{activeImage.originalFileName}
					</div>
				</div>
			) : null}
			{formattedFileSize ? (
				<div className="image-gallery-viewer__meta-item">
					<div className="image-gallery-viewer__meta-label">{t('chat.imageFileSize')}</div>
					<div className="image-gallery-viewer__meta-value">{formattedFileSize}</div>
				</div>
			) : null}
		</>
	);

	const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
		touchStartXRef.current = event.changedTouches[0]?.clientX || null;
	};

	const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
		const touchStartX = touchStartXRef.current;
		const touchEndX = event.changedTouches[0]?.clientX || null;
		touchStartXRef.current = null;

		if (touchStartX === null || touchEndX === null) {
			return;
		}

		const deltaX = touchEndX - touchStartX;
		if (Math.abs(deltaX) < SWIPE_THRESHOLD) {
			return;
		}

		if (deltaX < 0 && normalizedIndex < images.length - 1) {
			requestIndexChange(normalizedIndex + 1);
		}

		if (deltaX > 0 && normalizedIndex > 0) {
			requestIndexChange(normalizedIndex - 1);
		}
	};

	const handleDownload = () => {
		const downloadLink = document.createElement('a');
		downloadLink.href = activeImage.imageUrl;
		downloadLink.rel = 'noreferrer';
		downloadLink.target = '_blank';
		downloadLink.download = activeImage.originalFileName || 'image';
		document.body.append(downloadLink);
		downloadLink.click();
		setTimeout(() => {
			downloadLink.remove();
		}, 100);
	};

	return createPortal(
		<div className="image-gallery-viewer" role="dialog" aria-modal="true">
			<button
				type="button"
				className="image-gallery-viewer__backdrop"
				aria-label={t('chat.closeImageGallery')}
				onClick={onClose}
			/>
			<div className="image-gallery-viewer__content">
				{showMetaPanel ? (
					<div className="image-gallery-viewer__meta-container">
						<div className="image-gallery-viewer__meta">
							<div className="image-gallery-viewer__meta-topline">
								{activeImage.authorName ? (
									<div className="image-gallery-viewer__meta-author">
										<Avatar
											src={activeImage.authorAvatar || null}
											name={activeImage.authorName}
											size="md"
										/>
										<div className="image-gallery-viewer__meta-text">
											<div className="image-gallery-viewer__meta-label">
												{t('chat.imageAuthor')}
											</div>
											<div className="image-gallery-viewer__meta-value">
												{activeImage.authorName}
											</div>
										</div>
									</div>
								) : null}
								{!isMobileView && showDetailsSection ? (
									<div className="image-gallery-viewer__meta-main">{detailsContent}</div>
								) : null}
								<div className="image-gallery-viewer__meta-actions">
									{isMobileView ? (
										<Button
											label={t('chat.imageDetails')}
											icon="circle-info"
											iconOnly
											variant="white-text"
											variantSize="lg"
											circled
											className="image-gallery-viewer__meta-action-button"
											onClick={toggleMobileDetails}
										/>
									) : null}
									<Button
										label={t('chat.downloadImage')}
										icon="download"
										iconOnly={isMobileView}
										variant="white-text"
										variantSize={isMobileView ? 'lg' : 'sm'}
										circled={isMobileView}
										className="image-gallery-viewer__meta-action-button"
										onClick={handleDownload}
									/>
									{isMobileView ? (
										<Button
											label={t('chat.closeImageGallery')}
											icon="times"
											iconOnly
											variant="white-text"
											variantSize="lg"
											circled
											className="image-gallery-viewer__meta-action-button"
											onClick={onClose}
										/>
									) : null}
								</div>
							</div>
							{isMobileView && showDetailsSection ? (
								<div className="image-gallery-viewer__meta-main">{detailsContent}</div>
							) : null}
						</div>

						<div className="image-gallery-viewer__close-floating">
							<Button
								label={t('chat.closeImageGallery')}
								icon="times"
								iconOnly
								variant="white-text"
								variantSize="lg"
								circled
								className={`image-gallery-viewer__close-button${isMobileView ? ' image-gallery-viewer__close-button-hidden' : ''}`}
								onClick={onClose}
							/>
						</div>
					</div>
				) : null}
				<div
					className="image-gallery-viewer__main-stage"
					onTouchStart={handleTouchStart}
					onTouchEnd={handleTouchEnd}
				>
					{showSideArrows ? (
						<div
							className={`image-gallery-viewer__nav image-gallery-viewer__nav-prev${mobileNavStateClassName}`}
						>
							<Button
								label={t('chat.previousImage')}
								icon="chevron-left"
								iconOnly
								variant="white-text"
								variantSize="lg"
								circled
								disabled={normalizedIndex === 0}
								className="image-gallery-viewer__nav-button"
								onClick={() => requestIndexChange(normalizedIndex - 1)}
							/>
						</div>
					) : null}
					{showSideArrows ? (
						<div
							className={`image-gallery-viewer__nav image-gallery-viewer__nav-next${mobileNavStateClassName}`}
						>
							<Button
								label={t('chat.nextImage')}
								icon="chevron-right"
								iconOnly
								variant="white-text"
								variantSize="lg"
								circled
								disabled={normalizedIndex >= images.length - 1}
								className="image-gallery-viewer__nav-button"
								onClick={() => requestIndexChange(normalizedIndex + 1)}
							/>
						</div>
					) : null}
					{leavingImage ? (
						<img
							key={`leaving-${leavingImage.id || leavingImage.imageUrl}`}
							ref={leavingImageRef}
							src={leavingImage.imageUrl}
							alt=""
							className="image-gallery-viewer__main image-gallery-viewer__main-leaving"
						/>
					) : null}
					<img
						key={`active-${renderedImage.id || renderedImage.imageUrl}`}
						ref={enteringImageRef}
						src={renderedImage.imageUrl}
						alt=""
						className="image-gallery-viewer__main image-gallery-viewer__main-entering"
					/>
				</div>
				{images.length > 1 ? (
					<div className="image-gallery-viewer__thumbs">
						{images.map((image, index) => (
							<button
								key={image.id || `${image.thumbUrl}-${index}`}
								type="button"
								ref={(element) => {
									thumbRefs.current[index] = element;
								}}
								className={`image-gallery-viewer__thumb${index === normalizedIndex ? ' is-active' : ''}`}
								onClick={() => requestIndexChange(index)}
							>
								<img src={image.thumbUrl} alt="" />
							</button>
						))}
					</div>
				) : null}
			</div>
		</div>,
		document.body
	);
};
